import { useState, useEffect, useCallback } from 'react';
import { Download, AlertTriangle, Mail } from 'lucide-react';
import LoadingSpinner from './common/LoadingSpinner';
import ErrorMessage from './common/ErrorMessage';
import SearchBar from './common/SearchBar';
import { getMyDeliveries } from '../services/deliveryService';
import { useTeam } from '../context/TeamContext';
import './MyDeliveries.css';

const PAGE_SIZE = 25;

/** Colour-coded status badge */
function StatusBadge({ status }) {
  let className = 'delivery-status-badge';
  if (status === 'sent') className += ' status-sent';
  else if (status === 'bounced' || status === 'failed') className += ' status-failed';
  else className += ' status-other';

  return (
    <span className={className}>
      {status}
      {status === 'bounced' && (
        <span className="bounced-warning" title="Email could not be delivered to the recipient">
          <AlertTriangle size={12} />
        </span>
      )}
    </span>
  );
}

export default function MyDeliveries() {
  /* ── State ── */
  const { activeTeamId } = useTeam();
  const [deliveries, setDeliveries] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  /* ── Debounce search (400ms) ── */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /* ── Reset page when filters change ── */
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, dateFrom, dateTo, activeTeamId]);

  /* ── Fetch deliveries ── */
  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getMyDeliveries({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
        dateFrom,
        dateTo,
        teamId: activeTeamId || ''
      });
      setDeliveries(result.deliveries);
      setPagination(result.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, dateFrom, dateTo, activeTeamId]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  /* ── Download handler ── */
  const handleDownload = (signedUrl) => {
    window.open(signedUrl, '_blank', 'noopener,noreferrer');
  };

  /* ── Render ── */
  return (
    <div className="my-deliveries">
      {/* Controls: search + date filter */}
      <div className="my-deliveries-controls">
        <SearchBar
          value={searchTerm}
          onChange={(e) => setSearchTerm(typeof e === 'string' ? e : e.target.value)}
          placeholder="Search by report name..."
        />
        <div className="my-deliveries-date-filter">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            title="From date"
            aria-label="From date"
          />
          <span>–</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            title="To date"
            aria-label="To date"
          />
        </div>
      </div>

      {/* Summary */}
      {!loading && !error && deliveries.length > 0 && (
        <div className="my-deliveries-summary">
          {pagination.total} delivery{pagination.total !== 1 ? 'ies' : 'y'} found
          {' · '}Page {pagination.page} of {pagination.totalPages}
        </div>
      )}

      {/* Loading */}
      {loading && <LoadingSpinner />}

      {/* Error */}
      {!loading && error && (
        <ErrorMessage message={error} onRetry={fetchDeliveries} />
      )}

      {/* Empty state */}
      {!loading && !error && deliveries.length === 0 && (
        <div className="my-deliveries-empty">
          <Mail size={40} />
          <p>No email deliveries found for your account. Reports sent to your email address will appear here.</p>
        </div>
      )}

      {/* Deliveries table */}
      {!loading && !error && deliveries.length > 0 && (
        <>
          <table className="my-deliveries-table">
            <thead>
              <tr>
                <th>Report Name</th>
                <th>Delivery Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d) => (
                <tr key={d.deliveryId}>
                  <td>
                    <div className="delivery-name-cell">
                      <span>{d.reportName}</span>
                      {d.isResend && <span className="resent-badge">Resent</span>}
                    </div>
                  </td>
                  <td>{d.timestamp ? new Date(d.timestamp).toLocaleString() : '-'}</td>
                  <td>
                    <div className="delivery-status-cell">
                      <StatusBadge status={d.deliveryStatus} />
                      {d.clickCount > 0 && (
                        <span className="click-count" title={`Opened ${d.clickCount} time${d.clickCount !== 1 ? 's' : ''}`}>
                          {d.clickCount}×
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {d.urlExpired === false ? (
                      <button
                        className="download-btn"
                        onClick={() => handleDownload(d.signedUrl)}
                        title="Download report"
                      >
                        <Download size={14} />
                        Download
                      </button>
                    ) : (
                      <span className="expired-badge">Expired</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="my-deliveries-pagination">
            <button
              className="pagination-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              className="pagination-btn"
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={pagination.page >= pagination.totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
